// Orchestrates one ifc-mesh-wasm session (open_ifc -> mesh_manifest -> mesh_chunk* -> close_ifc)
// against the same source bytes the pinned ifc-bim-json plan importer already observed. Mirrors
// this app's existing src/integration/local-ifc.ts orchestration for the box pipeline: this file
// drives the WASM session lifecycle and always closes it; the pure geometry decode it calls into
// lives in src/domain/ifc-mesh-import.ts.
import { accumulateMeshGeometry, type DecodedMeshChunk, type MeshExtractionGeometry } from "../domain/ifc-mesh-import";
import { decodeChunkHeader, decodeManifest, IfcEngineError, type MeshManifest } from "../domain/ifc-mesh-source";
import type { LocalIfcMeshImporter } from "./ifc-mesh-wasm";

export const LOCAL_IFC_MESH_ARTIFACT = {
  packageName: "ifc-mesh-wasm",
  version: "0.4.1",
  sourceRepository: "https://github.com/0xkaz/ifc-engine-core",
  sourceCommit: "d8147ed2778e190589843c875f2b428471cc9be2",
  wasmDigest: "bd29496774bde13a314b272e01fd8f44b8d3557acb941faec4fbcddce3154d0b"
} as const;

export interface MeshExtractionSuccess extends MeshExtractionGeometry {
  readonly state: "extracted";
  readonly manifest: MeshManifest;
}

export interface MeshExtractionRefusal {
  readonly state: "refused";
  readonly reason: string;
}

export type MeshExtractionOutcome = MeshExtractionSuccess | MeshExtractionRefusal;

function reasonOf(op: string, cause: unknown): string {
  // An IfcEngineError already carries its own "op: message" prefix from wherever the WASM layer
  // (src/integration/ifc-mesh-wasm.ts) raised it -- re-wrapping it here would double the prefix.
  if (cause instanceof IfcEngineError) return cause.message;
  return `${op}: ${cause instanceof Error ? cause.message : "mesh extraction failed unexpectedly."}`;
}

async function defaultLoadImporter(): Promise<LocalIfcMeshImporter> {
  return (await import("./ifc-mesh-wasm")).loadPinnedLocalIfcMeshImporter();
}

/**
 * Attempts mesh extraction for the same source bytes the plan importer already observed. Additive
 * and best-effort: any refusal (unsupported WASM init, unopenable/unreadable IFC bytes, an
 * unsupported manifest/chunk wire version) is returned as a typed `refused` outcome, never thrown,
 * so a caller can fall back to the existing plan-derived box projection without losing the plan
 * pipeline's own observation. The WASM session is always closed, success or failure.
 *
 * Named tests: "extracts a single-chunk manifest into merged geometry" and
 * "returns a typed refusal instead of throwing when the importer cannot open the source".
 */
export async function extractLocalIfcMesh(
  bytes: Uint8Array,
  loadImporter: () => Promise<LocalIfcMeshImporter> = defaultLoadImporter
): Promise<MeshExtractionOutcome> {
  let importer: LocalIfcMeshImporter;
  try {
    importer = await loadImporter();
  } catch (cause) {
    return { state: "refused", reason: reasonOf("wasm_init", cause) };
  }

  let handle: number;
  try {
    handle = importer.open(bytes);
  } catch (cause) {
    return { state: "refused", reason: reasonOf("open_ifc", cause) };
  }

  try {
    let manifest: MeshManifest;
    try {
      manifest = decodeManifest(importer.manifest(handle));
    } catch (cause) {
      return { state: "refused", reason: reasonOf("mesh_manifest", cause) };
    }

    const chunks: DecodedMeshChunk[] = [];
    for (const manifestChunk of manifest.chunks) {
      let raw;
      try {
        raw = importer.chunk(handle, manifestChunk.chunk_id);
      } catch (cause) {
        return { state: "refused", reason: reasonOf(`mesh_chunk[${manifestChunk.chunk_id}]`, cause) };
      }
      let header;
      try {
        header = decodeChunkHeader(raw.header);
      } catch (cause) {
        return { state: "refused", reason: reasonOf(`mesh_chunk[${manifestChunk.chunk_id}].header`, cause) };
      }
      chunks.push({ manifestChunk, header, blob: raw.blob });
    }

    return { state: "extracted", manifest, ...accumulateMeshGeometry(chunks) };
  } finally {
    importer.close(handle);
  }
}
